import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CopyCheck, ChevronRight, Loader2, AlertTriangle, ExternalLink, Trash2, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, inr } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const REASON = { same_details: "Same student, mobile, page & fee", same_transaction: "Same transaction ID" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—");

const FIELDS = [
  ["Registration ID", (r) => r.registration_id], ["Registered at", (r) => fmt(r.created_at)], ["Student", (r) => r.student_name],
  ["Parent", (r) => r.parent_name], ["Mobile", (r) => r.mobile], ["Email", (r) => r.email], ["School", (r) => r.school_name],
  ["Page", (r) => r.page_title], ["Program", (r) => [r.program_name, r.level_name, r.batch_name].filter(Boolean).join(" · ")],
  ["Fee", (r) => inr(r.amount)], ["Paid", (r) => inr(r.total_paid)],
];

function PaymentList({ r, shared }) {
  return (
    <div className="space-y-1.5">
      {r.payments.map((p) => {
        const dup = p.utr && shared.includes(String(p.utr).toUpperCase());
        return (
          <div key={p.payment_id} className={`rounded-lg border px-2.5 py-2 text-xs ${dup ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="flex justify-between font-semibold text-slate-800"><span>{inr(p.paid_amount ?? p.amount)}</span><span className="uppercase text-[10px] text-slate-500">{p.status}</span></div>
            <div className={`font-mono mt-0.5 break-all ${dup ? "text-rose-700 font-semibold" : "text-slate-600"}`}>Txn: {p.utr || "—"}{dup && " (shared)"}</div>
            <div className="text-slate-500 mt-0.5">{p.verification_method === "email" ? "Email auto-verified" : p.verified_by ? `Verified by ${p.verified_by}` : "Screenshot auto-verified"} · {fmt(p.verified_at || p.created_at)}</div>
            {p.payee_name && <div className="text-slate-500">Payer: {p.payee_name}</div>}
          </div>
        );
      })}
      {r.payments.length === 0 && <div className="text-xs text-slate-400">No payments</div>}
    </div>
  );
}

function GroupDialog({ g, onClose, onDone }) {
  const [keep, setKeep] = useState(() => new Set(g ? [g.records[0].id] : []));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (g) setKeep(new Set([g.records[0].id])); }, [g]);
  if (!g) return null;
  const differs = (fn, label) => !["Registration ID", "Registered at"].includes(label) && new Set(g.records.map((r) => String(fn(r) ?? ""))).size > 1;
  const toggle = (id) => setKeep((k) => { const n = new Set(k); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const resolve = async (ids, label) => {
    const binCount = g.records.length - ids.length;
    const msg = binCount ? `${label}: keep ${ids.length}, move ${binCount} to the Bin (restorable for 10 days)?` : `${label}: keep all ${ids.length} as separate registrations?`;
    if (!window.confirm(msg + (g.shared_utrs.length && ids.length > 1 ? "\n\nKept copies that share one transaction ID will lose that payment and go back to pending." : ""))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/duplicates/${g.gid}/resolve`, { keep_ids: ids });
      toast.success(`Kept ${data.kept.join(", ")}${data.binned.length ? ` · Binned ${data.binned.join(", ")}` : ""}${data.reopened.length ? ` · Back to pending: ${data.reopened.join(", ")}` : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!g} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="dup-dialog">
        <DialogHeader><DialogTitle>Compare {g.records.length} registrations — {g.student_name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {g.reasons.map((r) => <span key={r} className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">{REASON[r]}</span>)}
          {g.shared_utrs.length > 0 && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> One transaction credited {g.records.length}× — money probably received once</span>}
        </div>
        <p className="sm:hidden text-xs text-slate-500">Swipe sideways to see every copy →</p>
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full text-sm border-separate border-spacing-0 min-w-[640px]">
            <thead>
              <tr>
                <th className="w-36" />
                {g.records.map((r, i) => (
                  <th key={r.id} className={`p-3 text-left align-top rounded-t-xl border-x border-t ${keep.has(r.id) ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
                    <label className="flex items-center gap-2 cursor-pointer" data-testid={`dup-keep-${r.registration_id}`}>
                      <input type="checkbox" checked={keep.has(r.id)} onChange={() => toggle(r.id)} className="w-4 h-4 accent-emerald-600" />
                      <span className="font-semibold text-slate-800">{keep.has(r.id) ? "Keep" : "Move to Bin"}</span>
                    </label>
                    <div className="text-[11px] font-normal text-slate-500 mt-1">{i === 0 ? "Earliest" : `Copy ${i + 1}`}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELDS.map(([label, fn]) => {
                const diff = differs(fn, label);
                return (
                  <tr key={label}>
                    <td className="py-2 pr-3 text-xs font-medium text-slate-500 align-top">{label}{diff && <span className="ml-1 text-amber-600">●</span>}</td>
                    {g.records.map((r) => <td key={r.id} className={`py-2 px-3 border-x align-top break-words ${keep.has(r.id) ? "border-emerald-300" : "border-slate-200"} ${diff ? "bg-amber-50/60 font-medium" : ""}`}>{fn(r) || "—"}</td>)}
                  </tr>
                );
              })}
              <tr>
                <td className="py-2 pr-3 text-xs font-medium text-slate-500 align-top">Payments</td>
                {g.records.map((r) => (
                  <td key={r.id} className={`p-3 border-x border-b rounded-b-xl align-top ${keep.has(r.id) ? "border-emerald-300" : "border-slate-200"}`}>
                    <PaymentList r={r} shared={g.shared_utrs} />
                    <Link to={`/admin/registrations/${r.id}`} target="_blank" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid={`dup-open-${r.registration_id}`}><ExternalLink className="w-3 h-3" /> Open to edit manually</Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2 border-t border-slate-100">
          <button disabled={busy} onClick={() => resolve(g.records.map((r) => r.id), "Not duplicates")} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2" data-testid="dup-keep-all-btn"><CheckCircle2 className="w-4 h-4" /> Keep all (not duplicates)</button>
          <button disabled={busy} onClick={() => resolve([g.records[0].id], "Keep earliest")} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2" data-testid="dup-keep-earliest-btn"><Trash2 className="w-4 h-4" /> Keep earliest, bin the rest</button>
          <button disabled={busy || keep.size === 0} onClick={() => resolve([...keep], "Keep selected")} className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2" data-testid="dup-keep-selected-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CopyCheck className="w-4 h-4" />} Keep selected ({keep.size}), bin others</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Duplicates() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/duplicates").then(({ data }) => setItems(data)).catch((e) => { setItems([]); toast.error(apiError(e.response?.data?.detail)); }), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="duplicates-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Duplicates</h1>
          <p className="text-sm text-slate-500 mt-1">Completed registrations that look like the same enrolment. Open a group to compare and decide.</p>
        </div>
        <button onClick={() => { setItems(null); load(); }} className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="dup-refresh-btn"><RefreshCw className="w-4 h-4" /> Rescan</button>
      </div>
      {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div> : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center" data-testid="dup-empty"><CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" /><div className="mt-3 font-semibold text-slate-800">No duplicates found</div><div className="text-sm text-slate-500">Every completed registration is unique.</div></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="dup-list">
          {items.map((g) => (
            <button key={g.gid} onClick={() => setOpen(g)} className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`dup-group-${g.records[0].registration_id}`}>
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold flex-shrink-0">{g.records.length}×</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{g.student_name || "—"} <span className="font-normal text-slate-400 text-sm">· {g.mobile}</span></div>
                <div className="text-xs text-slate-500 truncate">{[g.school_name, g.page_title].filter(Boolean).join(" · ")} · {inr(g.amount)} · {g.records.map((r) => r.registration_id).join(", ")}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {g.reasons.map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{REASON[r]}</span>)}
                  {g.shared_utrs.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Txn {g.shared_utrs[0]}</span>}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          ))}
        </div>
      )}
      <GroupDialog g={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />
    </div>
  );
}
